import { defineStore } from 'pinia';
import { useSchoolQuery } from '@/composables/queries/useSchoolQuery';
import { SetupStep } from '@/constants/setup-steps';
import { QUERY_ALL_SESSION_STRUCTURES } from '@/graphql';

export const useSchoolSetupStore = defineStore('schoolSetup', () => {
  const isWizardOpen = ref(false);

  // We use existing composables.
  const { school, loadingSchool: schoolLoading, refetchSchool } = useSchoolQuery();
  const { result: sessionStructuresResult, loading: structuresLoading } = QUERY_ALL_SESSION_STRUCTURES();

  const loading = computed(() => schoolLoading.value || structuresLoading.value);

  const schoolType = computed(() => school.value?.school_type?.toLowerCase() || '');
  const isTertiary = computed(() => schoolType.value === 'tertiary');

  const stepsStatus = computed(() => {
    const status: Record<string, boolean> = {
      [SetupStep.SESSION_STRUCTURE]: false,
      [SetupStep.SESSION]: false,
      [SetupStep.ACADEMIC_CALENDAR]: false,
      [SetupStep.CLASSROOMS]: false,
      [SetupStep.TERTIARY_FACULTY]: false,
      [SetupStep.TERTIARY_DEPARTMENT]: false,
      [SetupStep.TERTIARY_PROGRAMME]: false,
    };

    if (!school.value) return status;

    // Check Session Structure
    if (sessionStructuresResult.value?.allSessionStructures?.length > 0) {
      status[SetupStep.SESSION_STRUCTURE] = true;
    }

    // Check Academic Calendar (implies Session)
    if (school.value.academicCalendars && school.value.academicCalendars.length > 0) {
      status[SetupStep.SESSION] = true;
      status[SetupStep.ACADEMIC_CALENDAR] = true;
    }

    // Check Classrooms & Sections
    if (school.value.classroomSections && school.value.classroomSections.length > 0) {
      status[SetupStep.CLASSROOMS] = true;
    }

    return status;
  });

  const getRequiredSteps = () => {
    const steps = [
      SetupStep.SESSION_STRUCTURE,
      SetupStep.SESSION,
      SetupStep.ACADEMIC_CALENDAR,
    ];
    if (!isTertiary.value) {
      steps.push(SetupStep.CLASSROOMS);
    } else {
      // Add tertiary steps if we can check them
    }
    return steps;
  };

  const isSetupComplete = computed(() => {
    if (loading.value) return true; // Don't show incomplete while loading
    if (!school.value) return false;

    const requiredSteps = getRequiredSteps();
    return requiredSteps.every(step => stepsStatus.value[step]);
  });

  const firstIncompleteStep = computed(() => {
    const requiredSteps = getRequiredSteps();
    return requiredSteps.find(step => !stepsStatus.value[step]);
  });

  function openWizard() {
    isWizardOpen.value = true;
  }

  function closeWizard() {
    isWizardOpen.value = false;
    refetchSchool();
  }

  return {
    isWizardOpen,
    stepsStatus,
    isSetupComplete,
    firstIncompleteStep,
    loading,
    schoolType,
    isTertiary,
    openWizard,
    closeWizard,
    refetchSchool,
  };
});
